import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { HeadingNode } from '@lexical/rich-text';
import { $generateHtmlFromNodes } from '@lexical/html';

import ExampleTheme from './ExampleTheme';
import { ToolbarPlugin } from './plugins/ToolbarPlugin';
import EmojisPlugin from './plugins/EmojisPlugin';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { EmojiNode } from './nodes/EmojiNode';
import { useEffect, useState } from 'react';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin/FloatingLinkEditorPlugin';
import { TextEditorType } from '../types';
import { InputLabel } from '../../../index';
import { $getSelection, $isRangeSelection, $selectAll } from 'lexical';

const CAN_USE_DOM: boolean =
  typeof window !== 'undefined' &&
  typeof window.document !== 'undefined' &&
  typeof window.document.createElement !== 'undefined';

const editorConfig = {
  namespace: 'Text Editor',
  nodes: [EmojiNode, LinkNode, ListNode, ListItemNode, HeadingNode],
  onError(error: Error) {
    throw error;
  },
  theme: ExampleTheme,
};

export const TextEditor = ({
  label,
  placeholder,
  required,
  id,
  value,
  onChange,
}: TextEditorType) => {
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] =
    useState<boolean>(false);
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia('(max-width: 1025px)').matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  return (
    <>
      {label && <InputLabel text={label} name={id || ''} required={required} />}
      <LexicalComposer initialConfig={editorConfig}>
        <div className="editor-container">
          <ToolbarPlugin
            value={value || ''}
            setIsLinkEditMode={setIsLinkEditMode}
          />
          <div className="editor-inner">
            <RichTextPlugin
              contentEditable={
                <div className="editor" ref={onRef}>
                  <ContentEditable className="editor-input" />
                </div>
              }
              placeholder={
                <div className="editor-placeholder">
                  <ReturnTranslatedText
                    text={`inputs.placeholders.${placeholder}`}
                    origintext={placeholder}
                  />
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <AutoFocusPlugin />
            <EmojisPlugin />
            <LinkPlugin />
            <ListPlugin />
            {floatingAnchorElem && !isSmallWidthViewport && (
              <FloatingLinkEditorPlugin
                anchorElem={floatingAnchorElem}
                isLinkEditMode={isLinkEditMode}
                setIsLinkEditMode={setIsLinkEditMode}
              />
            )}
            <OnChangePlugin
              onChange={(state, editor) => {
                state.read(() => {
                  onChange && onChange($generateHtmlFromNodes(editor, null));
                });
              }}
            />
          </div>
        </div>
      </LexicalComposer>
    </>
  );
};
